import type { VenueView } from '@sorted/types'
import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from './icons.js'
import { VenueCard } from './VenueCard.js'

/**
 * The options, one at a time, swipeable.
 *
 * Full cards rather than a compact list: at three, the photo and map are what
 * make a place recognisable, and a stack of three abbreviated cards was a wall
 * of text you scrolled past.
 *
 * Native scroll-snap, not a carousel library - the browser already does
 * momentum, snapping and accessibility better than a dependency would, and
 * this ships as a handful of CSS lines.
 *
 * Dots AND arrows, because a swipe is invisible: on a phone it is discoverable
 * enough, on a desktop it is not a gesture at all. The dots also answer the
 * question the swipe raises, which is how many there are.
 */

interface Props {
  venues: VenueView[]
  token: string
  busy?: boolean
  cta: string
  onPick: (placeId: string) => void
}

export function VenueSlider({ venues, token, busy, cta, onPick }: Props) {
  const track = useRef<HTMLDivElement>(null)
  const [index, setIndex] = useState(0)

  // Follow the scroll position rather than owning it: the user can swipe, tap
  // a dot, or use an arrow, and all three have to agree on where we are.
  useEffect(() => {
    const el = track.current
    if (!el) return

    let frame = 0
    const onScroll = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const width = el.clientWidth
        if (width > 0) setIndex(Math.round(el.scrollLeft / width))
      })
    }

    el.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      cancelAnimationFrame(frame)
      el.removeEventListener('scroll', onScroll)
    }
  }, [])

  function goTo(i: number) {
    const el = track.current
    if (!el) return
    const clamped = Math.max(0, Math.min(i, venues.length - 1))
    el.scrollTo({ left: clamped * el.clientWidth, behavior: 'smooth' })
  }

  const single = venues.length === 1

  return (
    <div className="slider">
      {!single && (
        <div className="slider-nav">
          <button
            className="slider-arrow"
            onClick={() => goTo(index - 1)}
            disabled={index === 0}
            aria-label="Previous"
          >
            <ChevronLeft size={18} aria-hidden />
          </button>

          <div className="dots">
            {venues.map((v, i) => (
              <button
                key={v.placeId}
                className="dot"
                data-active={i === index}
                onClick={() => goTo(i)}
                aria-label={v.name}
                aria-current={i === index ? 'true' : undefined}
              />
            ))}
          </div>

          <button
            className="slider-arrow"
            onClick={() => goTo(index + 1)}
            disabled={index === venues.length - 1}
            aria-label="Next"
          >
            <ChevronRight size={18} aria-hidden />
          </button>
        </div>
      )}
      <div className="slider-track" ref={track}>
        {venues.map((v) => (
          <div className="slide" key={v.placeId}>
            <VenueCard
              venue={v}
              token={token}
              expanded
              busy={busy}
              cta={cta}
              onPick={() => onPick(v.placeId)}
            />
          </div>
        ))}
      </div>

    </div>
  )
}
